import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import StorageIcon from '@mui/icons-material/Storage';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import { useOrganizationSelect } from '../../../context/OrganizationSelectContext';
import PageHeader from '../../../components/common/PageHeader';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(Number(value) ? Number(value) * 1000 : value);
  if (Number.isNaN(date.getTime())) return '-';
  return `${date.toLocaleDateString('fr-FR')} ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};

const getStateColor = (state) => {
  const value = String(state || '').toLowerCase();
  if (['0', 'up', 'ok'].includes(value)) return 'success';
  if (['1', 'down', 'warning'].includes(value)) return 'warning';
  if (['2', 'critical', 'unreachable'].includes(value)) return 'error';
  return 'default';
};

const getStateLabel = (state) => {
  const value = String(state || '').toLowerCase();
  if (value === '0') return 'UP';
  if (value === '1') return 'WARNING';
  if (value === '2') return 'CRITICAL';
  return state || '-';
};

const Centreon = () => {
  const [centreon, setCentreon] = useState([]);
  const [hostgroups, setHostgroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hostgroupsLoading, setHostgroupsLoading] = useState(false);
  const [syncingHosts, setSyncingHosts] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { selectedOrganization } = useOrganizationSelect();

  const fetchCentreon = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/centreon`);
      let filteredData = response.data.filter((item) => item.deleted == null);
      if (selectedOrganization) {
        filteredData = response.data.filter((item) => item.organizationDetails?.name === selectedOrganization && item.deleted == null);
      }
      setCentreon(filteredData);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Impossible de charger Centreon.');
    } finally {
      setLoading(false);
    }
  };

  const fetchHostgroups = async () => {
    try {
      setHostgroupsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/centreon/hostgroups`);
      setHostgroups(response.data.groups || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Impossible de charger les hostgroups Centreon.');
    } finally {
      setHostgroupsLoading(false);
    }
  };

  const syncHosts = async () => {
    try {
      setSyncingHosts(true);
      setError('');
      setMessage('');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/centreon/hosts/sync`);
      setMessage(response.data.message || `Synchronisation terminée : ${response.data.hosts || 0} hôte(s).`);
      await fetchHostgroups();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Synchronisation des hôtes Centreon impossible.');
    } finally {
      setSyncingHosts(false);
    }
  };

  const formatValuecheckCritical = (value, critical) => {
    let formattedValue = value;
    if (typeof value === 'number') {
      formattedValue = value.toFixed(2).replace('.', ',');
    } else if (typeof value === 'string') {
      const num = parseFloat(value.replace(',', '.'));
      formattedValue = Number.isNaN(num) ? value : num.toFixed(2).replace('.', ',');
    }
    const valueNum = parseFloat(typeof value === 'string' ? value.replace(',', '.') : value) || 0;
    const criticalNum = parseFloat(typeof critical === 'string' ? critical.replace(',', '.') : critical) || 0;
    const isOverCritical = valueNum > criticalNum;
    return { isOverCritical, formattedValue };
  };

  useEffect(() => {
    fetchCentreon();
    fetchHostgroups();
  }, [selectedOrganization]);

  const filteredHostgroups = useMemo(() => {
    if (!selectedOrganization) return hostgroups;
    return hostgroups.filter((group) => group.organization?.name === selectedOrganization);
  }, [hostgroups, selectedOrganization]);

  const hostCount = filteredHostgroups.reduce((sum, group) => sum + (group.hostsCount || 0), 0);


  const hostRows = useMemo(() => filteredHostgroups.flatMap((group) => (
    (group.hosts || []).map((host) => ({
      ...host,
      id: `${group.id}-${host.id || host.name}`,
      client: group.organization?.name || 'Client non rattaché',
      hostgroup: group.name || '-',
      hostgroupAlias: group.alias || '',
      hostsCount: group.hostsCount || 0,
      groupError: group.error || '',
    }))
  )), [filteredHostgroups]);

  const hostColumns = useMemo(
    () => [
      {
        accessorKey: 'client',
        header: 'Client',
        size: 220,
        Cell: ({ renderedCellValue }) => <Box sx={{ fontWeight: 700 }}>{renderedCellValue || '-'}</Box>,
      },
      {
        accessorKey: 'hostgroup',
        header: 'Hostgroup',
        size: 180,
        Cell: ({ row }) => (
          <Stack spacing={0.25}>
            <Box sx={{ fontWeight: 700 }}>{row.original.hostgroup || '-'}</Box>
            {row.original.hostgroupAlias ? <Typography variant="caption" color="text.secondary">{row.original.hostgroupAlias}</Typography> : null}
          </Stack>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Hôte',
        size: 220,
        Cell: ({ renderedCellValue }) => <Box sx={{ fontWeight: 800 }}>{renderedCellValue || '-'}</Box>,
      },
      {
        accessorKey: 'alias',
        header: 'Alias',
        size: 180,
        Cell: ({ renderedCellValue }) => <Box>{renderedCellValue || '-'}</Box>,
      },
      {
        accessorKey: 'address',
        header: 'Adresse',
        size: 140,
        Cell: ({ renderedCellValue }) => <Box>{renderedCellValue || '-'}</Box>,
      },
      {
        accessorKey: 'state',
        header: 'État',
        size: 110,
        filterVariant: 'select',
        filterSelectOptions: ['0', '1', '2', 'UP', 'WARNING', 'CRITICAL'],
        Cell: ({ cell }) => <Chip size="small" color={getStateColor(cell.getValue())} label={getStateLabel(cell.getValue())} />,
      },
      {
        accessorFn: (row) => formatDateTime(row.lastCheck),
        id: 'lastCheck',
        header: 'Dernier check',
        size: 150,
        Cell: ({ renderedCellValue }) => <Box>{renderedCellValue || '-'}</Box>,
      },
      {
        accessorKey: 'output',
        header: 'Output',
        size: 420,
        Cell: ({ renderedCellValue }) => (
          <Typography variant="body2" sx={{ whiteSpace: 'normal', lineHeight: 1.35 }}>
            {renderedCellValue || '-'}
          </Typography>
        ),
      },
    ],
    [],
  );

  const hostTable = useMaterialReactTable({
    localization: MRT_Localization_FR,
    columns: hostColumns,
    data: hostRows,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    initialState: {
      density: 'compact',
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 50 },
      sorting: [{ id: 'client', desc: false }, { id: 'hostgroup', desc: false }, { id: 'name', desc: false }],
    },
    state: { isLoading: hostgroupsLoading },
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    muiSearchTextFieldProps: { size: 'small', variant: 'outlined' },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [50, 100, 150, 250],
      shape: 'rounded',
      variant: 'outlined',
    },
  });

  const columns = useMemo(
    () => [
      {
        id: 'centreon',
        columns: [
          {
            accessorFn: (row) => row.name,
            id: 'name',
            header: 'Nom',
            Cell: ({ renderedCellValue }) => <Box>{renderedCellValue}</Box>,
          },
          {
            accessorFn: (row) => row.value,
            id: 'size',
            header: 'Taille (Go)',
            size: 50,
            Cell: ({ row }) => {
              const { isOverCritical, formattedValue } = formatValuecheckCritical(row.original.value, row.original.critical);
              return (
                <Box display="flex" alignItems="center" color={isOverCritical ? 'red' : 'inherit'}>
                  {isOverCritical ? <HighlightOffIcon fontSize="small" style={{ marginRight: 4 }} /> : null}
                  {formattedValue}
                </Box>
              );
            },
          },
          {
            accessorFn: (row) => row.critical,
            id: 'critical',
            header: 'Taille max (Go)',
            size: 50,
            Cell: ({ renderedCellValue }) => <Box>{renderedCellValue}</Box>,
          },
          {
            accessorFn: (row) => row.organizationDetails?.name,
            id: 'organizationDetailsName',
            header: 'Client',
            Cell: ({ renderedCellValue }) => <Box>{renderedCellValue}</Box>,
          },
          {
            accessorFn: (row) => row.output,
            id: 'output',
            header: 'Output',
            Cell: ({ renderedCellValue }) => <Box>{renderedCellValue}</Box>,
          },
          {
            accessorFn: (row) => formatDateTime(row.lastCheck),
            id: 'lastCheck',
            header: 'Last Check',
            Cell: ({ renderedCellValue }) => <Box>{renderedCellValue}</Box>,
          },
        ],
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    localization: MRT_Localization_FR,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    columns,
    data: centreon || [],
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    initialState: {
      density: 'compact',
      showColumnFilters: true,
      showGlobalFilter: true,
      columnPinning: {
        left: ['mrt-row-expand', 'mrt-row-select'],
        right: ['mrt-row-actions'],
      },
      pagination: { pageSize: 50 },
    },
    state: { isLoading: loading },
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    muiSearchTextFieldProps: { size: 'small', variant: 'outlined' },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [50, 100, 150],
      shape: 'rounded',
      variant: 'outlined',
    },
  });

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" gap={2}>
          <PageHeader section="Infrastructure" title="Centreon" />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="outlined" onClick={() => { fetchCentreon(); fetchHostgroups(); }} disabled={loading || hostgroupsLoading || syncingHosts}>
              Actualiser
            </Button>
            <Button variant="contained" onClick={syncHosts} disabled={syncingHosts}>
              {syncingHosts ? <CircularProgress size={18} color="inherit" /> : 'Synchroniser'}
            </Button>
          </Stack>
        </Stack>

        {message ? <Alert severity="success" onClose={() => setMessage('')}>{message}</Alert> : null}
        {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <Paper variant="outlined" sx={{ p: 2, minWidth: 220 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <MonitorHeartIcon color="primary" />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>{filteredHostgroups.length}</Typography>
                <Typography variant="body2" color="text.secondary">Hostgroup(s) rattaché(s)</Typography>
              </Box>
            </Stack>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, minWidth: 220 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <StorageIcon color="primary" />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>{hostCount}</Typography>
                <Typography variant="body2" color="text.secondary">Hôte(s) Centreon</Typography>
              </Box>
            </Stack>
          </Paper>
        </Stack>

        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} spacing={1} sx={{ mb: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>Hôtes Centreon</Typography>
              <Typography variant="caption" color="text.secondary">Données issues de la dernière synchronisation DB.</Typography>
            </Box>
            {hostgroupsLoading ? <CircularProgress size={22} /> : <Chip variant="outlined" label={`${filteredHostgroups.length} groupe(s) / ${hostRows.length} hôte(s)`} />}
          </Stack>
          {!filteredHostgroups.length ? (
            <Alert severity="info" sx={{ mb: 1.5 }}>Aucun hostgroup Centreon mappé à un client. Le mapping se règle dans Paramètres &gt; Centreon.</Alert>
          ) : null}
          <MaterialReactTable table={hostTable} />
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>Stockage Centreon</Typography>
          <MaterialReactTable table={table} />
        </Paper>
      </Stack>
    </Box>
  );
};

export default Centreon;
